import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Grid, IconButton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { InputField, InputFieldComponent } from '../formItem';
import Button from '../button';
import Dropdown from '../dropdown';
import CommonCard from '../card/CommonCard';
import ContentArea from '../contentArea';
import { organizationAPI, userAPI, codeAPI } from '../../actions/imsServer';
import { ORGANIZATION_TYPES, COUNTRY_OPTIONS, getRegionOptions } from '../../constants';
import Popup from '../popup';
import Toast from '../toast';
import { useInterval } from '../../actions/interval';
import { Delete } from '../../assets/images';
import MapDialog from '../mapDialog';
import OrganizationTypeEditDialog from '../organizationTypeEditDialog';
import { searchAddress } from '../../utils/addressUtils';
import { isEmail, isValidPassword, isZipcode5, isPhone } from '../../utils/password-validation';

export const OrganizationApply = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        // 기관 정보
        name: '',
        loginId: '',
        verificationCode: '',
        loginPwd: '',
        loginPwdConfirm: '',
        type: '',
        typeLabel: '',
        divisionSection1: '',
        divisionSection2: '',
        divisionSections: {},
        // 담당자 정보
        userName: '',
        contactInfo: '',
        phone: '',
        email: '',
        // 주소 정보
        country: 'ko',
        zipcode: '',
        address: '',
        addressDetail: '',
        latitude: '',
        longitude: '',
        region: '',
        // 기타
        file: null,
    });

    const [isLoading, setIsLoading] = useState(false);
    const [popupOpen, setPopupOpen] = useState(false);
    const [popupMessage, setPopupMessage] = useState('');
    const [popupSubMessage, setPopupSubMessage] = useState('');
    const [shouldNavigateToLogin, setShouldNavigateToLogin] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });

    // 이메일 인증 관련
    const [countdown, setCountdown] = useState(0);
    const [isCodeVerified, setIsCodeVerified] = useState(false);
    const [isSendingCode, setIsSendingCode] = useState(false);
    const [isVerifyingCode, setIsVerifyingCode] = useState(false);
    const [loginIdExists, setLoginIdExists] = useState(false);
    const [isCheckingLoginId, setIsCheckingLoginId] = useState(false);

    // 기관 유형 편집 모드
    const [editDialogOpen, setEditDialogOpen] = useState(false);

    // 파일 업로드 상태
    const [uploadedFiles, setUploadedFiles] = useState([]);

    // 지도 팝업 상태
    const [mapDialogOpen, setMapDialogOpen] = useState(false);
    const [mapPosition, setMapPosition] = useState([37.5665, 126.9780]); // 기본 위치 (서울)

    // 카운트다운 타이머
    useInterval(() => {
        if (countdown > 0) {
            setCountdown(countdown - 1);
        }
    }, countdown > 0 ? 1000 : null);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // 계정 중복 확인
    const checkLoginIdExists = async (loginId) => {
        if (!loginId || !validateEmail(loginId)) {
            setLoginIdExists(false);
            return;
        }

        try {
            setIsCheckingLoginId(true);
            const response = await userAPI.checkUserExists({ loginId: loginId });
            setLoginIdExists(response.data.exists || false);
        } catch (error) {
            console.error('계정 중복 확인 실패:', error);
            setLoginIdExists(false);
        } finally {
            setIsCheckingLoginId(false);
        }
    };

    // 계정 입력 변경 핸들러 (디바운스 적용)
    const handleLoginIdChange = (value) => {
        const previousAccount = formData.loginId;
        handleInputChange('loginId', value);
        // 관리자 계정 이메일을 담당자 메일에 자동 입력
        handleInputChange('email', value);

        // 관리자 계정이 변경되면 인증 상태 초기화
        if (previousAccount !== value && isCodeVerified) {
            setIsCodeVerified(false);
            handleInputChange('verificationCode', '');
            setCountdown(0);
        }

        // 디바운스를 위해 기존 타이머 클리어
        if (window.loginIdCheckTimer) {
            clearTimeout(window.loginIdCheckTimer);
        }

        // 500ms 후에 중복 확인 실행
        window.loginIdCheckTimer = setTimeout(() => {
            checkLoginIdExists(value);
        }, 500);
    };

    const showPopup = (message, subMessage = '') => {
        setPopupMessage(message);
        setPopupSubMessage(subMessage);
        setPopupOpen(true);
    };

    const handleClosePopup = () => {
        setPopupOpen(false);
        // 가입신청 성공 팝업인 경우 로그인 페이지로 이동
        if (shouldNavigateToLogin) {
            setShouldNavigateToLogin(false);
            navigate('/auth/signin');
        }
    };

    // 토스트 메시지 표시
    const showToast = (message, severity = 'success') => {
        setSnackbar({
            open: true,
            message,
            severity,
        });
    };

    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    // 유효성 검사 함수들
    const validateName = (name) => {
        return name && name.length >= 2 && name.length <= 20;
    };

    const validateEmail = (email) => email && isEmail(email);
    const validateLoginPwd = (loginPwd) => loginPwd && isValidPassword(loginPwd);
    const validateZipcode = (zipcode) => zipcode && isZipcode5(zipcode);
    const validatePhone = (phone) => phone && isPhone(phone);

    // 이메일 인증 코드 전송
    const handleSendCode = async () => {
        if (!formData.loginId.trim()) {
            showToast(t('please_enter_organization_admin_account'));
            return;
        }

        if (!validateEmail(formData.loginId.trim())) {
            showToast(t('please_enter_a_valid_email_address'));
            return;
        }

        setIsSendingCode(true);
        setCountdown(60);

        try {
            // 계정 중복 확인
            const checkResponse = await userAPI.checkUserExists({
                loginId: formData.loginId.trim()
            });

            if (checkResponse.data.exists === true) {
                showToast(t('already_exists'));
                setCountdown(0);
                return;
            }

            // 인증 코드 발송
            await codeAPI.generateCode({
                target: formData.loginId.trim(),
                type: 0 // 이메일 타입
            });

            showToast(t('verification_code_sent_to_email', { email: formData.loginId }));
        } catch (error) {
            console.error('인증코드 전송 실패:', error);
            showToast(t('failed_to_send_verification_code'), t('please_try_again'));
            setCountdown(0);
        } finally {
            setIsSendingCode(false);
        }
    };

    // 인증 코드 확인
    const handleVerifyCode = async () => {
        if (!formData.verificationCode.trim()) {
            showToast(t('please_enter_verification_code'));
            return;
        }

        if (formData.verificationCode.trim().length !== 6) {
            showToast(t('verification_code_must_be_6_digits'));
            return;
        }

        setIsVerifyingCode(true);
        try {
            const response = await codeAPI.confirmCode({
                target: formData.loginId.trim(),
                type: 0,
                key: formData.verificationCode.trim()
            });

            if (response.result === 'ok') {
                setIsCodeVerified(true);
                showToast(t('verification_complete'));
            } else {
                showToast(t('verification_code_does_not_match'));
            }
        } catch (error) {
            console.error('인증코드 확인 실패:', error);
            showToast(t('verification_check_failed'), t('please_try_again'));
        } finally {
            setIsVerifyingCode(false);
        }
    };

    // 주소 검색 핸들러 (우편번호 또는 주소 일부로 검색 가능)
    const handleAddressSearch = async (searchQuery) => {
        await searchAddress(searchQuery, formData.country, (addressData) => {
            setFormData(prev => ({
                ...prev,
                zipcode: addressData.zipcode || prev.zipcode,
                address: addressData.address,
                latitude: addressData.latitude,
                longitude: addressData.longitude,
                region: addressData.region
            }));
        });
    };

    // 우편번호 검색 핸들러 (기존 호환성을 위해 유지)
    const handleZipcodeSearch = async () => {
        await handleAddressSearch(formData.zipcode);
    };

    // 지도 팝업 핸들러
    const handleOpenMapDialog = () => {
        // 현재 좌표가 있으면 해당 위치로 설정, 없으면 기본 위치
        const currentLat = parseFloat(formData.latitude) || 37.5665;
        const currentLng = parseFloat(formData.longitude) || 126.9780;
        setMapPosition([currentLat, currentLng]);
        setMapDialogOpen(true);
    };

    const handleCloseMapDialog = () => {
        setMapDialogOpen(false);
    };

    const handleMapPositionSave = (position) => {
        setFormData(prev => ({
            ...prev,
            latitude: position[0].toString(),
            longitude: position[1].toString()
        }));
        showToast(t('latitude_and_longitude_have_been_updated'));
        setMapDialogOpen(false);
    };

    // 파일 업로드 핸들러
    const handleFileUpload = (event) => {
        const files = Array.from(event.target.files);

        if (files.length > 1) {
            showToast(t('maximum_1_attachment_allowed'));
            return;
        }

        const validFiles = files.filter(file => {
            if (file.size > 10 * 1024 * 1024) {
                showToast(t('file_size_cannot_exceed_10mb'));
                return false;
            }
            const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
            if (!allowedTypes.includes(file.type)) {
                showToast(t('only_pdf_jpeg_png_files_are_allowed'));
                return false;
            }
            return true;
        });

        if (validFiles.length === 0) return;

        const newFile = validFiles[0];
        const previewUrl = URL.createObjectURL(newFile);

        const fileWithPreview = {
            name: newFile.name,
            size: newFile.size,
            type: newFile.type,
            file: newFile,
            previewUrl: previewUrl,
            isExisting: false
        };

        setUploadedFiles([fileWithPreview]);
        setFormData(prev => ({
            ...prev,
            file: newFile
        }));
    };

    // 드래그 앤 드롭 핸들러
    const handleDragOver = (event) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const handleDragEnter = (event) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const handleDragLeave = (event) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const handleDrop = (event) => {
        event.preventDefault();
        event.stopPropagation();

        const files = Array.from(event.dataTransfer.files);

        if (files.length > 1) {
            showToast(t('maximum_1_attachment_allowed'));
            return;
        }

        const validFiles = files.filter(file => {
            if (file.size > 10 * 1024 * 1024) {
                showToast(t('file_size_cannot_exceed_10mb'));
                return false;
            }
            const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
            if (!allowedTypes.includes(file.type)) {
                showToast(t('only_pdf_jpeg_png_files_are_allowed'));
                return false;
            }
            return true;
        });

        if (validFiles.length === 0) return;

        const newFile = validFiles[0];
        const previewUrl = URL.createObjectURL(newFile);

        const fileWithPreview = {
            name: newFile.name,
            size: newFile.size,
            type: newFile.type,
            file: newFile,
            previewUrl: previewUrl,
            isExisting: false
        };

        setUploadedFiles([fileWithPreview]);
        setFormData(prev => ({
            ...prev,
            file: newFile
        }));
    };

    // 기관유형 편집 팝업 핸들러
    const handleEditOrganizationType = () => {
        setEditDialogOpen(true);
    };

    const handleCloseEditDialog = () => {
        setEditDialogOpen(false);
    };

    const handleSaveOrganizationType = (data) => {
        const orgType = ORGANIZATION_TYPES.find(type => type.value === data.type);
        const organizationTypeLabel = orgType ? orgType.label : '';

        // divisionSection1, divisionSection2 계산
        const divisionSections = data.divisionSections || {};
        const gradeCount = Object.keys(divisionSections).length;
        const divisionSection1 = gradeCount.toString();

        // divisionSection2 계산
        let divisionSection2 = '0';
        if (gradeCount > 0 && orgType && orgType.grades && orgType.grades.length > 1) {
            const classCounts = Object.values(divisionSections).map(classes =>
                Array.isArray(classes) ? classes.length : 0
            );
            divisionSection2 = classCounts.join(',');
        }

        setFormData(prev => ({
            ...prev,
            type: data.type,
            typeLabel: organizationTypeLabel,
            divisionSection1,
            divisionSection2,
            divisionSections: divisionSections
        }));

        setEditDialogOpen(false);
    };



    // 폼 유효성 검증 (팝업 없이 검증만 수행)
    const isFormValid = useMemo(() => {
        if (!validateName(formData.name)) return false;
        if (!formData.loginId.trim()) return false;
        if (!validateEmail(formData.loginId.trim())) return false;
        if (loginIdExists) return false;
        if (!isCodeVerified) return false;
        if (!formData.loginPwd.trim()) return false;
        if (!validateLoginPwd(formData.loginPwd.trim())) return false;
        if (formData.loginPwd !== formData.loginPwdConfirm) return false;
        if (!formData.type) return false;
        if (!formData.userName.trim()) return false;
        if (!formData.contactInfo.trim()) return false;
        if (!formData.phone.trim()) return false;
        if (!validatePhone(formData.phone.trim())) return false;
        if (!formData.email.trim()) return false;
        if (!validateEmail(formData.email.trim())) return false;
        if (!validateZipcode(formData.zipcode)) return false;
        if (!formData.address.trim()) return false;
        if (!formData.latitude || !formData.longitude) return false;
        if (!formData.region) return false;
        if (!formData.file) return false;
        return true;
    }, [
        formData.name,
        formData.loginId,
        formData.loginPwd,
        formData.loginPwdConfirm,
        formData.type,
        formData.userName,
        formData.contactInfo,
        formData.phone,
        formData.email,
        formData.zipcode,
        formData.address,
        formData.latitude,
        formData.longitude,
        formData.region,
        formData.file,
        loginIdExists,
        isCodeVerified
    ]);

    // 폼 유효성 검증 (팝업 포함)
    const validateForm = () => {
        if (!validateName(formData.name)) {
            showToast(t('organization_name_must_be_2_to_20_characters'));
            return false;
        }
        if (!formData.loginId.trim()) {
            showToast(t('please_enter_organization_admin_account'));
            return false;
        }
        if (!validateEmail(formData.loginId.trim())) {
            showToast(t('please_enter_a_valid_email_address'));
            return false;
        }
        if (loginIdExists) {
            showToast(t('already_exists'));
            return false;
        }
        if (!isCodeVerified) {
            showToast(t('please_verify_your_email_address'));
            return false;
        }
        if (!formData.loginPwd.trim()) {
            showToast(t('please_enter_your_password'));
            return false;
        }
        if (!validateLoginPwd(formData.loginPwd.trim())) {
            showToast(t('include_at_least_2_types_of_letters_numbers_and_symbols_6_12_characters'));
            return false;
        }
        if (formData.loginPwd !== formData.loginPwdConfirm) {
            showToast(t('passwords_do_not_match'));
            return false;
        }
        if (!formData.type) {
            showToast(t('please_select_organization_type'));
            return false;
        }
        if (!formData.userName.trim()) {
            showToast(t('enter_contact_name'));
            return false;
        }
        if (!formData.contactInfo.trim()) {
            showToast(t('please_enter_contact_title'));
            return false;
        }
        if (!formData.phone.trim()) {
            showToast(t('please_enter_contact_phone'));
            return false;
        }
        if (!validatePhone(formData.phone.trim())) {
            showToast(t('please_enter_phone_number_numbers_only'), t('please_enter_9_11_digit_phone_number'));
            return false;
        }
        if (!formData.email.trim()) {
            showToast(t('please_enter_contact_email'));
            return false;
        }
        if (!validateEmail(formData.email.trim())) {
            showToast(t('please_enter_a_valid_email_address'));
            return false;
        }
        if (!validateZipcode(formData.zipcode)) {
            showToast(t('please_enter_a_valid_zip_code'));
            return false;
        }
        if (!formData.address.trim()) {
            showToast(t('enter_address'));
            return false;
        }
        if (!formData.latitude || !formData.longitude) {
            showToast(t('please_enter_latitude_and_longitude'));
            return false;
        }
        if (!formData.region) {
            showToast(t('please_select_region'));
            return false;
        }
        if (!formData.file) {
            showToast(t('please_upload_attachment'));
            return false;
        }
        return true;
    };

    // 제출
    const handleSubmit = async () => {
        if (!validateForm()) return;

        setIsLoading(true);
        try {
            // 기관 신청 (관리자 계정 생성 + 기관 생성)
            const organizationApplyData = {
                loginId: formData.loginId.trim(),
                loginPwd: formData.loginPwd.trim(),
                userName: formData.userName.trim(),
                name: formData.name.trim(),
                type: formData.type,
                address: formData.address.trim(),
                addressDetail: formData.addressDetail.trim(),
                zipcode: formData.zipcode.trim(),
                phone: formData.phone.trim(),
                email: formData.email.trim(),
                country: formData.country,
                region: formData.region,
                latitude: formData.latitude,
                longitude: formData.longitude,
                divisionSections: JSON.stringify(formData.divisionSections),
                contactInfo: formData.contactInfo,
                file: formData.file
            };

            await organizationAPI.applyOrganization(organizationApplyData);

            setShouldNavigateToLogin(true);
            showPopup(t('your_application_has_been_received_we_will_notify_you_of_the_approval_or_rejection_status_as_soon_as_possible'));

            // 성공 후 폼 초기화
            setTimeout(() => {
                setFormData({
                    name: '',
                    loginId: '',
                    verificationCode: '',
                    loginPwd: '',
                    loginPwdConfirm: '',
                    type: '',
                    typeLabel: '',
                    divisionSection1: '',
                    divisionSection2: '',
                    divisionSections: {},
                    userName: '',
                    contactInfo: '',
                    phone: '',
                    email: '',
                    country: 'ko',
                    zipcode: '',
                    address: '',
                    addressDetail: '',
                    latitude: '',
                    longitude: '',
                    region: '',
                    file: null,
                });
                setIsCodeVerified(false);
                setCountdown(0);
                setUploadedFiles([]);
            }, 2000);
        } catch (error) {
            console.error('기관 가입 신청 실패:', error);
            const errorMessage = error.response?.data?.message || t('organization_application_failed');
            showPopup(errorMessage, t('please_try_again'));
        } finally {
            setIsLoading(false);
        }
    };

    // 컴포넌트 언마운트 시 URL 정리
    useEffect(() => {
        return () => {
            uploadedFiles.forEach(file => {
                if (file.previewUrl && !file.isExisting) {
                    URL.revokeObjectURL(file.previewUrl);
                }
            });
        };
    }, [uploadedFiles]);

    return (
        <ContentArea padding='20px 10px 10px 10px'>
            {/* 제목 */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                mb: 4,
                mt: 2
            }}>
                <Typography sx={{
                    fontSize: '24px',
                    fontWeight: 700,
                    textAlign: 'center',
                }}>
                    {t('organization_application_form')}
                </Typography>
            </Box>

            <Box sx={{ px: 3, pb: 4, width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Grid container spacing={4} sx={{ flex: 1 }}>
                    {/* 왼쪽 컬럼 - 기관 정보 및 담당자 정보 */}
                    <Grid item xs={12} md={6} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        {/* 기관 정보 섹션 */}
                        <CommonCard sx={{ alignItems: 'stretch', gap: 3, mb: 4 }}>
                            <InputFieldComponent title={`${t('organization_name')} *`}>
                                <InputField
                                    placeholder={t('organization_name_2_20_characters')}
                                    value={formData.name}
                                    onChange={(e) => handleInputChange('name', e.target.value)}
                                    error={formData.name && !validateName(formData.name)}
                                    helperText={formData.name && !validateName(formData.name) ? t('organization_name_must_be_2_to_20_characters') : ' '}
                                />
                            </InputFieldComponent>

                            <InputFieldComponent title={t('organization_admin_account')}>
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                                    <InputField
                                        placeholder={t('email_format')}
                                        value={formData.loginId}
                                        onChange={(e) => handleLoginIdChange(e.target.value)}
                                        error={formData.loginId && (!validateEmail(formData.loginId) || loginIdExists)}
                                        helperText={
                                            (formData.loginId && !validateEmail(formData.loginId)
                                                ? t('please_enter_a_valid_email_address')
                                                : formData.loginId && loginIdExists
                                                    ? t('already_exists')
                                                    : formData.loginId && isCheckingLoginId
                                                        ? t('checking_account_availability')
                                                        : formData.loginId && !loginIdExists && validateEmail(formData.loginId)
                                                            ? t('account_is_available')
                                                            : ' ')
                                        }
                                        sx={{ flex: 1 }}
                                    />
                                    <Button
                                        size="small"
                                        onClick={handleSendCode}
                                        disabled={countdown > 0 || isSendingCode || !formData.loginId.trim() || !validateEmail(formData.loginId)}
                                    >
                                        {countdown > 0 ? `${countdown}${t('sec')}` : t('send_code')}
                                    </Button>
                                </Box>
                            </InputFieldComponent>
                            <InputFieldComponent title={t('verification_code') + ' *'}>
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                                    <InputField
                                        placeholder={t('6_digits')}
                                        value={formData.verificationCode}
                                        onChange={(e) => handleInputChange('verificationCode', e.target.value.replace(/\D/g, '').slice(0, 6))}
                                        maxLength={6}
                                        sx={{ flex: 1 }}
                                    />
                                    <Button
                                        size="small"
                                        onClick={handleVerifyCode}
                                        disabled={isVerifyingCode || isCodeVerified || !formData.verificationCode.trim() || formData.verificationCode.trim().length !== 6}
                                    >
                                        {t('confirm')}
                                    </Button>
                                </Box>
                            </InputFieldComponent>

                            <InputFieldComponent title={t('account_password') + ' *'}>
                                <InputField
                                    type="password"
                                    showVisibilityIcon={true}
                                    placeholder={t('include_at_least_2_types_of_letters_numbers_and_symbols_6_12_characters')}
                                    value={formData.loginPwd}
                                    onChange={(e) => handleInputChange('loginPwd', e.target.value)}
                                    error={formData.loginPwd && !validateLoginPwd(formData.loginPwd)}
                                    helperText={formData.loginPwd && !validateLoginPwd(formData.loginPwd) ? t('include_at_least_2_types_of_letters_numbers_and_symbols_6_12_characters') : ' '}
                                />
                            </InputFieldComponent>

                            <InputFieldComponent title={t('account_password_confirmation') + ' *'}>
                                <InputField
                                    type="password"
                                    showVisibilityIcon={true}
                                    placeholder={t('confirm_your_password')}
                                    value={formData.loginPwdConfirm}
                                    onChange={(e) => handleInputChange('loginPwdConfirm', e.target.value)}
                                    error={formData.loginPwd && formData.loginPwdConfirm && formData.loginPwd !== formData.loginPwdConfirm}
                                    helperText={formData.loginPwd && formData.loginPwdConfirm && formData.loginPwd !== formData.loginPwdConfirm ? t('passwords_do_not_match') : ' '}
                                />
                            </InputFieldComponent>

                            <InputFieldComponent title={t('organization_type') + ' *'}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <InputField
                                        placeholder={t('please_select')}
                                        disabled
                                        value={formData.typeLabel ? t(formData.typeLabel) : formData.type}
                                        showClearIcon={false}
                                        sx={{ flex: 1 }}
                                    />
                                    <Button size="small" onClick={handleEditOrganizationType}>
                                        {t('edit')}
                                    </Button>
                                </Box>
                            </InputFieldComponent>

                            <InputFieldComponent title={t('division_1')}>
                                <InputField
                                    placeholder={t('auto_filled_when_organization_type_is_edited')}
                                    disabled
                                    showClearIcon={false}
                                    value={formData.divisionSection1}
                                />
                            </InputFieldComponent>

                            <InputFieldComponent title={t('division_2')}>
                                <InputField
                                    placeholder={t('auto_filled_when_organization_type_is_edited')}
                                    disabled
                                    showClearIcon={false}
                                    value={formData.divisionSection2}
                                />
                            </InputFieldComponent>
                        </CommonCard>

                        {/* 담당자 정보 섹션 */}
                        <CommonCard sx={{ alignItems: 'stretch', gap: 3 }}>
                            <InputFieldComponent title={t('contact_name') + ' *'}>
                                <InputField
                                    placeholder={t('enter_name')}
                                    value={formData.userName}
                                    onChange={(e) => handleInputChange('userName', e.target.value)}
                                />
                            </InputFieldComponent>

                            <InputFieldComponent title={t('contact_title') + ' *'}>
                                <InputField
                                    placeholder={t('enter_contact_title')}
                                    value={formData.contactInfo}
                                    onChange={(e) => handleInputChange('contactInfo', e.target.value)}
                                />
                            </InputFieldComponent>

                            <InputFieldComponent title={t('contact_phone') + ' *'}>
                                <InputField
                                    placeholder={t('please_enter_phone_number_numbers_only')}
                                    value={formData.phone}
                                    onChange={(e) => handleInputChange('phone', e.target.value)}
                                    error={formData.phone && !validatePhone(formData.phone)}
                                    helperText={formData.phone && !validatePhone(formData.phone) ? t('please_enter_9_11_digit_phone_number') : ' '}
                                />
                            </InputFieldComponent>

                            <InputFieldComponent title={t('contact_email') + ' *'}>
                                <InputField
                                    type="email"
                                    placeholder={t('enter_contact_email')}
                                    value={formData.email}
                                    onChange={(e) => handleInputChange('email', e.target.value)}
                                    error={formData.email && !validateEmail(formData.email)}
                                    helperText={formData.email && !validateEmail(formData.email) ? t('please_enter_a_valid_email_address') : ' '}
                                />
                            </InputFieldComponent>

                            {(!uploadedFiles.length) && (
                                <InputFieldComponent title={`${t('attachment')} *`} sx={{ height: '128px' }}>
                                    <Box sx={{
                                        border: `2px dashed ${theme.palette.figma.white[20]}`,
                                        borderRadius: '8px',
                                        p: 3,
                                        textAlign: 'center',
                                        backgroundColor: theme.palette.figma.white[5],
                                        cursor: 'pointer',
                                        '&:hover': {
                                            backgroundColor: theme.palette.figma.white[10],
                                            borderColor: theme.palette.figma.white[30]
                                        }
                                    }}
                                        onDragOver={handleDragOver}
                                        onDragEnter={handleDragEnter}
                                        onDragLeave={handleDragLeave}
                                        onDrop={handleDrop}>
                                        <input
                                            type="file"
                                            accept=".pdf,.jpg,.jpeg,.png"
                                            onChange={handleFileUpload}
                                            style={{ display: 'none' }}
                                            id="file-upload"
                                        />
                                        <label htmlFor="file-upload" style={{ cursor: 'pointer', width: '100%', display: 'block' }}>
                                            <Typography sx={{
                                                color: theme.palette.figma.white[95],
                                                fontSize: '14px',
                                                mb: 1
                                            }}>
                                                {t('business_registration_or_employment_certificate')}
                                            </Typography>
                                            <Typography sx={{
                                                color: theme.palette.figma.white[60],
                                                fontSize: '14px',
                                                mb: 1
                                            }}>
                                                {t('drag_or_click_to_upload_file')}
                                            </Typography>
                                            <Typography sx={{
                                                color: theme.palette.figma.white[40],
                                                fontSize: '12px'
                                            }}>
                                                {t('pdf_jpeg_png_only_max_1_file_10mb')}
                                            </Typography>
                                        </label>
                                    </Box>
                                </InputFieldComponent>
                            )}

                            {/* 업로드된 파일 목록 */}
                            {uploadedFiles.length > 0 && (
                                <Box sx={{ mt: 2 }}>
                                    {uploadedFiles.map((file, index) => (
                                        <Box key={index} sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 2,
                                            p: 2,
                                            backgroundColor: theme.palette.figma.white[5],
                                            borderRadius: '6px',
                                            border: `1px solid ${theme.palette.figma.white[20]}`
                                        }}>
                                            {file.type && file.type.startsWith('image/') && file.previewUrl && (
                                                <Box sx={{
                                                    width: '60px',
                                                    height: '60px',
                                                    borderRadius: '6px',
                                                    overflow: 'hidden',
                                                    border: `1px solid ${theme.palette.figma.white[20]}`,
                                                    flexShrink: 0
                                                }}>
                                                    <img
                                                        src={file.previewUrl}
                                                        alt={file.name}
                                                        style={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover'
                                                        }}
                                                    />
                                                </Box>
                                            )}
                                            {file.type === 'application/pdf' && (
                                                <Box sx={{
                                                    width: '60px',
                                                    height: '60px',
                                                    borderRadius: '6px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    backgroundColor: theme.palette.figma.white[10],
                                                    border: `1px solid ${theme.palette.figma.white[20]}`,
                                                    flexShrink: 0
                                                }}>
                                                    <Typography sx={{
                                                        fontSize: '20px',
                                                        color: theme.palette.error.main,
                                                        fontWeight: 700
                                                    }}>
                                                        PDF
                                                    </Typography>
                                                </Box>
                                            )}
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography sx={{
                                                    fontSize: '12px',
                                                    color: theme.palette.figma.white[80],
                                                    fontWeight: 500,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}>
                                                    {file.name}
                                                </Typography>
                                                <Typography sx={{
                                                    fontSize: '11px',
                                                    color: theme.palette.figma.white[60],
                                                    mt: 0.5
                                                }}>
                                                    {`${(file.size / 1024).toFixed(0)}KB`}
                                                </Typography>
                                            </Box>
                                            <IconButton
                                                onClick={() => {
                                                    if (file.previewUrl) {
                                                        URL.revokeObjectURL(file.previewUrl);
                                                    }
                                                    setUploadedFiles([]);
                                                    setFormData(prev => ({ ...prev, file: null }));
                                                }}
                                                size="small"
                                            >
                                                <Delete
                                                    size={20}
                                                    color={theme.palette.figma.icon}
                                                />
                                            </IconButton>
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </CommonCard>
                    </Grid>

                    {/* 오른쪽 컬럼 - 주소 정보 */}
                    <Grid item xs={12} md={6} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <CommonCard sx={{ alignItems: 'stretch', gap: 3 }}>
                            <InputFieldComponent title={t('country') + ' *'}>
                                <Dropdown
                                    options={COUNTRY_OPTIONS}
                                    value={formData.country}
                                    onChange={(option) => handleInputChange('country', option.value)}
                                    placeholder={t('select_region')}
                                />
                            </InputFieldComponent>

                            <InputFieldComponent title={t('address') + ' *'}>
                                <InputField
                                    placeholder={formData.country === 'ko' ? t('enter_address') : t('enter_zip_code')}
                                    value={formData.address}
                                    onChange={(e) => handleInputChange('address', e.target.value)}
                                    onBlur={(e) => {
                                        // 대한민국이고 주소가 입력되었지만 우편번호가 없는 경우 주소 검색
                                        if (formData.country === 'ko' && e.target.value && e.target.value.trim().length >= 2) {
                                            handleAddressSearch(e.target.value);
                                        }
                                    }}
                                    onKeyDown={(e) => {
                                        // 엔터키 입력 시 주소 검색
                                        if (e.key === 'Enter' && formData.country === 'ko' && formData.address && formData.address.trim().length >= 2) {
                                            e.preventDefault();
                                            handleAddressSearch(formData.address);
                                        }
                                    }}
                                />
                            </InputFieldComponent>

                            <InputFieldComponent title={t('enter_address_detail')}>
                                <InputField
                                    placeholder={t('enter_address_detail')}
                                    value={formData.addressDetail}
                                    onChange={(e) => handleInputChange('addressDetail', e.target.value)}
                                />
                            </InputFieldComponent>

                            <InputFieldComponent title={t('enter_zip_code') + ' *'}>
                                <InputField
                                    placeholder={t('enter_zip_code')}
                                    value={formData.zipcode}
                                    onChange={(e) => handleInputChange('zipcode', e.target.value.replace(/\D/g, '').slice(0, 5))}
                                    onBlur={(e) => {
                                        if (e.target.value && e.target.value.trim().length >= 5) {
                                            handleZipcodeSearch();
                                        }
                                    }}
                                    error={formData.zipcode && !validateZipcode(formData.zipcode)}
                                    helperText={formData.zipcode && !validateZipcode(formData.zipcode) ? t('please_enter_a_valid_zip_code') : ' '}
                                />
                            </InputFieldComponent>

                            <InputFieldComponent title={t('enter_latitude') + ' *'}>
                                <InputField
                                    placeholder={t('enter_latitude')}
                                    value={formData.latitude}
                                    onChange={(e) => handleInputChange('latitude', e.target.value)}
                                />
                            </InputFieldComponent>

                            <InputFieldComponent title={t('enter_longitude') + ' *'}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <InputField
                                        placeholder={t('enter_longitude')}
                                        value={formData.longitude}
                                        onChange={(e) => handleInputChange('longitude', e.target.value)}
                                        sx={{ flex: 1 }}
                                    />
                                    <Button size="small" onClick={handleOpenMapDialog}>
                                        {t('edit')}
                                    </Button>
                                </Box>
                            </InputFieldComponent>

                            <InputFieldComponent title={t('select_region') + ' *'}>
                                <Dropdown
                                    options={getRegionOptions(formData.country)}
                                    value={formData.region}
                                    onChange={(option) => handleInputChange('region', option.value)}
                                    placeholder={t('please_select_region')}
                                />
                            </InputFieldComponent>

                            <Box sx={{ mt: 2 }}>
                                <Typography sx={{
                                    fontSize: '12px',
                                    color: theme.palette.figma.white[60],
                                    lineHeight: 1.6,
                                    textAlign: 'left'
                                }}>
                                    {t('indicates_required_fields')}<br />
                                    - {t('address_zip_code_and_region_are_auto_filled_some_may_require_manual_entry')}<br />
                                    - {t('latitude_and_longitude_are_auto_filled_from_address_zip_code_adjust_as_needed_for_accuracy')}
                                </Typography>
                            </Box>
                        </CommonCard>

                        {/* 하단 버튼 영역 */}
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'flex-end',
                            gap: 2,
                            px: 2.5,
                            py: 3,
                            alignSelf: 'stretch',
                            marginTop: 'auto'
                        }}>
                            <Button
                                variant="gray"
                                onClick={() => navigate(-1)}
                                sx={{ minWidth: '120px' }}
                            >
                                {t('cancel')}
                            </Button>
                            <Button
                                onClick={handleSubmit}
                                loading={isLoading}
                                sx={{ minWidth: '160px' }}
                                disabled={!isFormValid}
                            >
                                {t('submit_organization_application')}
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </Box>

            {/* 기관유형 편집 팝업 */}
            <OrganizationTypeEditDialog
                open={editDialogOpen}
                onClose={handleCloseEditDialog}
                onSave={handleSaveOrganizationType}
                initialType={formData.type}
                initialDivisionSections={formData.divisionSections}
            />

            {/* 지도 팝업 */}
            <MapDialog
                open={mapDialogOpen}
                onClose={handleCloseMapDialog}
                initialPosition={mapPosition}
                onSave={handleMapPositionSave}
            />

            <Popup
                open={popupOpen}
                message={popupMessage}
                subMessage={popupSubMessage}
                onConfirm={handleClosePopup}
            />

            {/* 토스트 메시지 */}
            <Toast
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={handleCloseSnackbar}
            />
        </ContentArea>
    );
};

export default OrganizationApply;
